import React, { useState, useEffect, useCallback } from 'react';
import { 
  Typography, 
  Table, 
  Tag, 
  Card, 
  Space, 
  Button, 
  Tooltip, 
  Input, 
  Select, 
  DatePicker,
  Row,
  Col,
  Badge
} from 'antd';
import { 
  ReloadOutlined, 
  SearchOutlined, 
  FilterOutlined
} from '@ant-design/icons';
import { useAdmin } from '../../hooks/useAdmin';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({
    action_type: null,
    target_type: null,
    search: ''
  });
  
  const { getGlobalAuditLogs, loading } = useAdmin();

  const loadLogs = useCallback(async () => {
    const res = await getGlobalAuditLogs({
      page: pagination.current,
      limit: pagination.pageSize,
      ...filters
    });

    if (res.success) {
      setLogs(res.data.logs);
      setPagination(prev => ({ ...prev, total: res.data.pagination.total }));
    }
  }, [getGlobalAuditLogs, pagination.current, pagination.pageSize, filters]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const getActionColor = (action) => {
    if (action.includes('create') || action.includes('add')) return 'green';
    if (action.includes('update') || action.includes('edit')) return 'blue';
    if (action.includes('delete') || action.includes('remove') || action.includes('reject')) return 'red';
    if (action.includes('approve') || action.includes('verify')) return 'gold';
    return 'default';
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date) => (
        <span className="text-slate-400 font-mono text-xs">
          {dayjs(date).format('DD/MM/YYYY HH:mm:ss')}
        </span>
      )
    },
    {
      title: 'Administrateur',
      dataIndex: 'admin_name',
      key: 'admin_name',
      width: 150,
      render: (name, r) => (
        <Space size="small">
          <Badge status="processing" />
          <Text strong className="text-slate-200">{name || r.admin_id.substring(0,8)}</Text>
        </Space>
      )
    },
    {
      title: 'Action',
      dataIndex: 'action_type',
      key: 'action_type',
      width: 150,
      render: (action) => (
        <Tag color={getActionColor(action)} className="uppercase font-bold text-[10px]">
          {action.replace('_', ' ')}
        </Tag>
      )
    },
    {
      title: 'Cible',
      dataIndex: 'target_type',
      key: 'target_type',
      width: 120,
      render: (type) => (
        <Text className="text-slate-400 uppercase text-[10px] tracking-widest">{type}</Text>
      )
    },
    {
      title: 'Détails',
      dataIndex: 'details',
      key: 'details',
      render: (details) => {
        const detailStr = typeof details === 'string' ? details : JSON.stringify(details);
        return (
          <Tooltip title={detailStr}>
            <div className="max-w-full max-w-[400px] truncate text-slate-400 text-sm italic">
              {detailStr}
            </div>
          </Tooltip>
        );
      }
    },
    {
      title: 'IP',
      dataIndex: 'ip_address',
      key: 'ip_address',
      width: 130,
      render: (ip) => <Text className="text-slate-500 font-mono text-xs">{ip || '0.0.0.0'}</Text>
    }
  ];

  return (
    <div className="admin-audit-inner animate-fade-in">
      <div className="glass-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={2} className="admin-page-title">Audit Logs</Title>
            <Text className="admin-page-subtitle">Traçabilité complète de toutes les actions administratives.</Text>
          </div>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={loadLogs} 
            loading={loading}
            className="glass-button"
          >
            Actualiser
          </Button>
        </div>
      </div>

      <Card className="glass-card mb-6" bodyStyle={{ padding: '20px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={6}>
            <div className="text-xs text-slate-500 uppercase font-bold mb-2">Type d'Action</div>
            <Select 
              placeholder="Tous les types" 
              style={{ width: '100%' }} 
              allowClear
              className="glass-select"
              onChange={(v) => setFilters(f => ({ ...f, action_type: v }))}
            >
              <Option value="update_balance">Mise à jour Solde</Option>
              <Option value="approve_withdrawal">Approbation Retrait</Option>
              <Option value="verify_kyc">Vérification KYC</Option>
              <Option value="update_plan">Modif. Plan</Option>
            </Select>
          </Col>
          <Col xs={24} md={6}>
            <div className="text-xs text-slate-500 uppercase font-bold mb-2">Période</div>
            <RangePicker className="glass-input w-full" />
          </Col>
          <Col xs={24} md={8}>
            <div className="text-xs text-slate-500 uppercase font-bold mb-2">Recherche Libre</div>
            <Input 
              prefix={<SearchOutlined />} 
              placeholder="Admin, ID cible, contenu..." 
              className="glass-input"
            />
          </Col>
          <Col xs={24} md={4}>
            <div className="mb-2">&nbsp;</div>
            <Button type="primary" block icon={<FilterOutlined />} className="glass-button" onClick={loadLogs}>
              Filtrer
            </Button>
          </Col>
        </Row>
      </Card>

      <div className="glass-card">
        <Table
          columns={columns}
          dataSource={logs.map(l => ({ ...l, key: l.id }))}
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            className: "glass-pagination",
            onChange: (page, pageSize) => setPagination({ ...pagination, current: page, pageSize })
          }}
          className="admin-table-custom"
          scroll={{ x: 1000 }}
        />
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .admin-table-custom .ant-table {
          background: transparent !important;
          color: #e2e8f0 !important;
        }
        .admin-table-custom .ant-table-thead > tr > th {
          background: rgba(15, 23, 42, 0.6) !important;
          color: #94a3b8 !important;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
          text-transform: uppercase;
          font-size: 11px;
          letter-spacing: 0.05em;
        }
        .admin-table-custom .ant-table-tbody > tr > td {
          border-bottom: 1px solid rgba(255, 255, 255, 0.02) !important;
        }
        .admin-table-custom .ant-table-tbody > tr:hover > td {
          background: rgba(255, 255, 255, 0.02) !important;
        }
      `}} />
    </div>
  );
};

export default AuditLogs;
